import type { Coordinates } from '$lib/components/map/types';
import type { Pagename } from '$lib/services/urls/routesPagename';
import { SUB_TYPE } from '$houdini';
import { SEARCH_EXTENSION_TOUCHPOINT } from '$lib/services/searchExtension';
import z from 'zod';

export const BOOKINGCOM_URL_ID = {
	poilist_fallback: 'poilist_fallback',
	boxmap_itisearch_generic: 'boxmap_itisearch_generic',
	boxmap_itisearch_with_arrival: 'boxmap_itisearch_with_arrival',
	boxmap_mapresult_with_arrival: 'boxmap_mapresult_with_arrival',
	boxmap_itiresult: 'boxmap_itiresult',
	poi_in_datasheet: 'poi_in_datasheet',
	poi_datasheet_availability: 'poi_datasheet_availability',
	poi_in_poilist: 'poi_in_poilist',
	arrival_itiresult: 'arrival_itiresult',
	search_extension: 'search_extension',
} as const;

export type BookingcomUrlId = keyof typeof BOOKINGCOM_URL_ID;

export type ViaMichelinWebTld =
	| 'at'
	| 'be'
	| 'ch'
	| 'com'
	| 'co.uk'
	| 'de'
	| 'es'
	| 'fr'
	| 'ie'
	| 'it'
	| 'nl'
	| 'pl'
	| 'pt';

export const BOOKINGCOM_DEVICE_TYPE = {
	PHONE: 'phone',
	TABLET: 'tablet',
	DESKTOP: 'desktop',
} as const;

export const BOOKINGCOM_ACCOMMODATION_CATEGORY = [...Object.values(SUB_TYPE)] as const;

export const field = {
	arrival: z.string().meta({
		description: 'ville d\'arrivée, juste le nom de la ville',
		examples: ['Lyon'],
	}),
	doAvailabilityCheck: z.enum(['On', 'Off']).meta({
		description: 'Recherche effectuée avec une recherche de disponibilité',
	}),
	departure: z.string().meta({
		description: 'ville de départ, juste le nom de la ville',
		examples: ['Paris'],
	}),
	contentType: z.enum(['datasheet', 'availability_datasheet', 'boxmap', 'search_extension', 'poilist', 'arrival_zoi']).meta({
		description: 'type de contenu',
	}),
	distance: z.number().meta({
		description: 'nombre de km de l\'itinéraire calculé avant la redirection',
		examples: [450],
	}),
	seTouchpoint: z.enum(SEARCH_EXTENSION_TOUCHPOINT).meta({
		description: 'type de déclencheur de la search extension',
	}),
} as const;

export const baseField = z.object({
	clientId: z.literal('viam').meta({
		description: 'nom du front sur lequel se trouve l\'url',
	}),
	tld: z.custom<ViaMichelinWebTld>().meta({
		description: 'extension du site VM web. Pour info, côté app, il s\'agit du code langue',
	}),
	deviceType: z.enum(BOOKINGCOM_DEVICE_TYPE).meta({
		description: 'nom du type de device',
	}),
	timestamp: z.string().meta({
		description: 'Unix timestamp as string',
	}),
	keys: z.array(z.string()).optional(),
	values: z.array(z.string()).optional(),
	trackingAcquisition: z.string().optional().meta({
		description: 'information qui remonte du paramètre origine= nécessaire pour web.de, les NL, le SEA, le magazine,...',
	}),
	pagename: z.custom<Pagename>().meta({
		description: 'Current pagename',
	}),
});

export type BookingcomLabelBaseData = z.infer<typeof baseField>;

export const poiField = {
	poiId: z.string().meta({
		description: 'identifiant du POI dans la BDD VM',
		examples: ['04fe45d4'],
	}),
	poiSrcId: z.string().meta({
		description: 'identifiant du POI dans la BDD Booking',
		examples: ['51245'],
	}),
	poiAccommodationSubType: z.enum(BOOKINGCOM_ACCOMMODATION_CATEGORY).meta({
		description: 'catégorie d\'hôtel',
	}),
	poiPrice: z.string().optional().meta({
		'description': 'prix remonté pour l\'hotel cliqué, optionnel pour les hébergements complets ou le prix est absent',
		'examples': ['55 €'],
		'x-notes': [
			'Empty when the price is not available',
		],
	}),
	poiStar: z.int().min(1).max(5).optional().meta({
		'description': 'nombre d\'étoiles de l\'hôtel',
		'x-notes': [
			'Empty when accommodation has no star assigned',
		],
	}),
} as const;

// Helper function to make all fields in a schema optional
function makeOptional<T extends Record<string, z.ZodTypeAny>>(schema: T) {
	return Object.fromEntries(
		Object.entries(schema).map(([key, value]) => [key, value.optional()]),
	) as { [K in keyof T]: z.ZodOptional<T[K]> };
};

// All fields schema with dynamic optional fields
const allFields = {
	...poiField,
	...field,
};
export const bookingLabelDataSchema = baseField.extend(allFields);
export const bookingLabelDataNullableSchema = bookingLabelDataSchema.extend({ ...makeOptional(allFields) });

export type BookingcomLabelData = z.infer<typeof bookingLabelDataSchema>;
export type BookingcomLabelDataNullable = z.infer<typeof bookingLabelDataNullableSchema>;
export type BookingcomLabelBuildData = Omit<BookingcomLabelData, keyof BookingcomLabelBaseData>;
export type BookingcomLabelBuildDataNullable = Omit<BookingcomLabelDataNullable, keyof BookingcomLabelBaseData>;
export interface BookingcomUrlBuildParams {
	arrivalCoordinates: Coordinates;
};

export const BOOKING_LABEL_SCHEMA: (keyof BookingcomLabelData)[] = [
	'clientId',
	'contentType',
	'tld',
	'pagename',
	'deviceType',
	'departure',
	'arrival',
	'poiPrice',
	'poiAccommodationSubType',
	'doAvailabilityCheck',
	'keys',
	'values',
	'poiId',
	'poiSrcId',
	'trackingAcquisition',
	'distance',
	'timestamp',
	'seTouchpoint',
	'poiStar',
];
